import { useCallback, useMemo, useRef, useState } from "react";

import { kaiApi, type KaiAttachment } from "@/services/kaiApi";
import type { KaiMessageMetadata } from "@/features/kai/kaiTypes";
import { buildUserDisplayContent, fileFingerprint } from "@/lib/kaiAttachments";

/** Client-side timeout while waiting for upload + server-side OCR/extraction. */
const UPLOAD_TIMEOUT_MS = 120_000;

/** One selected file and its upload/extraction lifecycle. */
export interface AttachmentEntry {
  file: File;
  conversationId: string;
  status: "uploading" | "done" | "failed";
  attachment?: KaiAttachment;
  error?: string;
}

interface UseKaiAttachmentsOptions {
  /**
   * Resolve the conversation the files belong to, creating one if needed.
   * Returns `null` if a conversation could not be established.
   */
  resolveConversationId: () => Promise<string | null>;
}

export interface UseKaiAttachments {
  entries: AttachmentEntry[];
  /** IDs of successfully extracted attachments, ready to send. */
  attachmentIds: string[];
  /** True while any file is still uploading/extracting. */
  uploading: boolean;
  /** True when sending should be blocked (an upload is in flight). */
  blocking: boolean;
  addFiles: (files: File[]) => Promise<void>;
  removeAt: (index: number) => void;
  clear: () => void;
  /** Snapshot attachment metadata for an optimistic user message bubble. */
  snapshotMetadata: () => KaiMessageMetadata | undefined;
  /** User-visible text for the outgoing bubble (includes upload-only turns). */
  displayContentFor: (content: string) => string;
}

/**
 * Manage Kai chat file attachments: upload selected files, track their
 * server-side extraction outcome, and expose the IDs to attach to the next
 * message. Shared by the main chat panel and the right-rail side panel.
 */
export function useKaiAttachments({
  resolveConversationId,
}: UseKaiAttachmentsOptions): UseKaiAttachments {
  const [entries, setEntries] = useState<AttachmentEntry[]>([]);
  const [uploading, setUploading] = useState(false);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  /** Fingerprints currently being uploaded (guards drop + picker double-fire). */
  const inFlightRef = useRef(new Set<string>());

  const addFiles = useCallback(
    async (selected: File[]) => {
      if (selected.length === 0) return;

      // Compute novel files synchronously — never via a setState side-channel,
      // which races when drop and picker onChange fire together.
      const committed = new Set(
        entriesRef.current.map((entry) => fileFingerprint(entry.file)),
      );
      const novelFiles = selected.filter((file) => {
        const fp = fileFingerprint(file);
        return !committed.has(fp) && !inFlightRef.current.has(fp);
      });
      if (novelFiles.length === 0) return;

      const novelFingerprints = new Set(novelFiles.map(fileFingerprint));
      for (const fp of novelFingerprints) {
        inFlightRef.current.add(fp);
      }

      let conversationId: string | null = null;
      try {
        conversationId = await resolveConversationId();
      } catch (err) {
        console.error(
          "Failed to resolve conversation for attachment upload",
          err,
        );
      }
      if (!conversationId) {
        for (const fp of novelFingerprints) {
          inFlightRef.current.delete(fp);
        }
        return;
      }
      const convId = conversationId;

      setEntries((prev) => [
        ...prev,
        ...novelFiles.map((file) => ({
          file,
          conversationId: convId,
          status: "uploading" as const,
        })),
      ]);

      setUploading(true);

      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        UPLOAD_TIMEOUT_MS,
      );

      try {
        const results = await kaiApi.uploadAttachments(convId, novelFiles, {
          signal: controller.signal,
        });
        const resultsByName = new Map(
          results.map((result) => [result.filename, result]),
        );
        setEntries((prev) =>
          prev.map((entry) => {
            const fp = fileFingerprint(entry.file);
            if (!novelFingerprints.has(fp) || entry.status !== "uploading") {
              return entry;
            }
            const idx = novelFiles.findIndex(
              (file) => fileFingerprint(file) === fp,
            );
            const result = results[idx] ?? resultsByName.get(entry.file.name);
            if (!result) {
              return {
                ...entry,
                status: "failed",
                error: "No extraction result returned",
              };
            }
            const done = result.extraction_status === "done";
            return {
              ...entry,
              attachment: result,
              status: done ? "done" : "failed",
              error: done
                ? undefined
                : (result.extraction_error ?? "Extraction failed"),
            };
          }),
        );
      } catch (err) {
        const message =
          err instanceof DOMException && err.name === "AbortError"
            ? "Upload timed out — image OCR can take a minute; try again or use a PDF/CSV instead"
            : err instanceof Error
              ? err.message
              : "Upload failed";
        setEntries((prev) =>
          prev.map((entry) =>
            novelFingerprints.has(fileFingerprint(entry.file)) &&
            entry.status === "uploading"
              ? { ...entry, status: "failed", error: message }
              : entry,
          ),
        );
      } finally {
        window.clearTimeout(timeoutId);
        setUploading(false);
        for (const fp of novelFingerprints) {
          inFlightRef.current.delete(fp);
        }
      }
    },
    [resolveConversationId],
  );

  const removeAt = useCallback((index: number) => {
    setEntries((prev) => {
      const entry = prev[index];
      if (entry?.attachment) {
        // Best-effort server-side cleanup; ignore failures.
        kaiApi
          .deleteAttachment(entry.conversationId, entry.attachment.id)
          .catch(() => undefined);
      }
      if (entry) {
        inFlightRef.current.delete(fileFingerprint(entry.file));
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const clear = useCallback(() => {
    inFlightRef.current.clear();
    setEntries([]);
  }, []);

  const attachmentIds = useMemo(
    () =>
      entries
        .filter((e) => e.status === "done" && e.attachment)
        .map((e) => e.attachment!.id),
    [entries],
  );
  const blocking = uploading || entries.some((e) => e.status === "uploading");

  const snapshotMetadata = useCallback((): KaiMessageMetadata | undefined => {
    const done = entries.filter((e) => e.status === "done" && e.attachment);
    if (done.length === 0) return undefined;
    return {
      attachment_ids: done.map((e) => e.attachment!.id),
      filenames: done.map((e) => e.attachment!.filename),
    };
  }, [entries]);

  const displayContentFor = useCallback(
    (content: string) => {
      const filenames = entries
        .filter((e) => e.status === "done" && e.attachment)
        .map((e) => e.attachment!.filename);
      return buildUserDisplayContent(content, filenames);
    },
    [entries],
  );

  return {
    entries,
    attachmentIds,
    uploading,
    blocking,
    addFiles,
    removeAt,
    clear,
    snapshotMetadata,
    displayContentFor,
  };
}
